import type { FrpPoint } from "../shared/series";
import { formatChartTick, formatFrp, formatLookClock } from "./format";

const W = 360;
const H = 108;
// Tight left gutter — 52px was empty space once the SVG filled the panel.
const L = 28;
const R = 8;
const T = 14;
const B = 28;

/** Compact FRP sparkline — dates on the axis when the series is not “today”. */
export function renderFrpChart(points: FrpPoint[], playheadMs?: number, nowMs = Date.now()): string {
  if (points.length < 2) {
    return `<p class="fine">Not enough overpasses yet for an FRP chart.</p>`;
  }
  const xs = points.map((p) => p.t);
  const ys = points.map((p) => p.mw);
  const x0 = xs[0]!;
  const x1 = xs[xs.length - 1]!;
  const y1 = Math.max(...ys, 1);
  const innerW = W - L - R;
  const innerH = H - T - B;

  const xOf = (t: number) => L + ((t - x0) / Math.max(x1 - x0, 1)) * innerW;
  const yOf = (mw: number) => T + innerH - (mw / y1) * innerH;

  const line = points.map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(p.t).toFixed(1)} ${yOf(p.mw).toFixed(1)}`).join(" ");
  const area = `${line} L ${xOf(x1).toFixed(1)} ${(T + innerH).toFixed(1)} L ${xOf(x0).toFixed(1)} ${(T + innerH).toFixed(1)} Z`;
  const mid = y1 / 2;
  const yTicks = [0, mid, y1];

  const startD = new Date(x0);
  const endD = new Date(x1);
  const nowD = new Date(nowMs);
  // Date on ticks when the series is not today, or when it crosses a local midnight.
  const dated =
    !sameLocalDay(startD, nowD) || !sameLocalDay(endD, nowD) || !sameLocalDay(startD, endD);
  const startIso = startD.toISOString();
  const endIso = endD.toISOString();

  // Fat invisible hit target so hover is usable; visible dot stays small.
  const dots = points
    .map((p) => {
      const cx = xOf(p.t).toFixed(1);
      const cy = yOf(p.mw).toFixed(1);
      const tip = `${formatLookClock(new Date(p.t).toISOString(), nowMs)} · ${formatFrp(p.mw)}`;
      return `<g class="frp-pt" data-frp="${escapeXml(tip)}" data-x="${cx}" data-y="${cy}">
        <circle class="frp-hit" cx="${cx}" cy="${cy}" r="9" fill="transparent"/>
        <circle class="frp-dot" cx="${cx}" cy="${cy}" r="2.5" fill="#ffb020"/>
      </g>`;
    })
    .join("");

  return `<div class="frp-chart-wrap">
    <svg class="frp-chart" viewBox="0 0 ${W} ${H}" width="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Fire radiative power over time">
      <line class="axis" x1="${L}" y1="${T}" x2="${L}" y2="${T + innerH}"/>
      <line class="axis" x1="${L}" y1="${T + innerH}" x2="${W - R}" y2="${T + innerH}"/>
      ${yTicks
        .map((v) => {
          const y = yOf(v);
          return `<line class="grid" x1="${L}" y1="${y.toFixed(1)}" x2="${W - R}" y2="${y.toFixed(1)}"/>
            <text class="tick" x="${L - 8}" y="${y + 3}" text-anchor="end">${escapeXml(tickMw(v))}</text>`;
        })
        .join("")}
      <path class="frp-fill" d="${area}"/>
      <path class="frp-line" d="${line}"/>
      ${
        playheadMs != null && playheadMs >= x0 && playheadMs <= x1
          ? `<line class="playhead" x1="${xOf(playheadMs).toFixed(1)}" y1="${T}" x2="${xOf(playheadMs).toFixed(1)}" y2="${T + innerH}"/>`
          : ""
      }
      ${dots}
      <text class="tick" x="${L}" y="${H - 6}">${escapeXml(formatChartTick(startIso, nowMs, dated))}</text>
      <text class="tick" x="${W - R}" y="${H - 6}" text-anchor="end">${escapeXml(formatChartTick(endIso, nowMs, dated))}</text>
    </svg>
    <div class="frp-hover hidden" role="status"></div>
  </div>`;
}

/** X of the playhead line in the viewBox, or null when off the series. */
export function frpPlayheadX(points: FrpPoint[], playheadMs: number): number | null {
  if (points.length < 2) return null;
  const x0 = points[0]!.t;
  const x1 = points[points.length - 1]!.t;
  if (playheadMs < x0 || playheadMs > x1) return null;
  const innerW = W - L - R;
  return L + ((playheadMs - x0) / Math.max(x1 - x0, 1)) * innerW;
}

/** Move the existing playhead <line> instead of replacing the SVG every frame. */
export function setFrpPlayheadLine(slot: Element | null, x: number | null): void {
  const svg = slot?.querySelector("svg.frp-chart");
  if (!svg) return;
  let line = svg.querySelector("line.playhead");
  if (x == null) {
    line?.remove();
    return;
  }
  if (!line) {
    line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("class", "playhead");
    line.setAttribute("y1", String(T));
    line.setAttribute("y2", String(T + H - T - B));
    svg.appendChild(line);
  }
  const xs = x.toFixed(1);
  line.setAttribute("x1", xs);
  line.setAttribute("x2", xs);
}

/** Bind hover after the slot’s innerHTML is replaced (playhead re-renders the SVG). */
export function bindFrpHover(slot: Element | null): void {
  if (!slot) return;
  const wrap = slot.querySelector(".frp-chart-wrap");
  const tip = slot.querySelector(".frp-hover");
  if (!(wrap instanceof HTMLElement) || !(tip instanceof HTMLElement)) return;
  for (const el of wrap.querySelectorAll(".frp-pt")) {
    if (!(el instanceof SVGElement)) continue;
    const show = () => {
      tip.textContent = el.dataset.frp ?? "";
      tip.classList.remove("hidden");
      const cx = Number(el.dataset.x);
      const cy = Number(el.dataset.y);
      if (!Number.isFinite(cx) || !Number.isFinite(cy)) return;
      tip.style.left = `${(cx / W) * 100}%`;
      tip.style.top = `${(cy / H) * 100}%`;
    };
    el.addEventListener("pointerenter", show);
    el.addEventListener("pointerleave", () => tip.classList.add("hidden"));
  }
}

function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function tickMw(v: number): string {
  if (v <= 0) return "0";
  if (v >= 10) return String(Math.round(v));
  return v.toFixed(1);
}

function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}
